import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/modules/auth";
import { useTodayHoliday } from "@/api/hooks/holidays";
import { useTodayAttendance } from "@/api/hooks/attendance";
import { useMe } from "@/api/hooks/auth";
import { GeofenceLiveMap, type FencePoint } from "./GeofenceLiveMap";

/**
 * Permanent prompt telling the user to mark in attendance after login.
 * Cannot be dismissed until /attendance/today returns a row.
 *
 * **Mark-in happens on the phone, not here.** The server refuses a mark-in
 * (and a mark-out) that is not signed by the employee's registered Workman
 * mobile app, so the web has no slider: it points the user at the app, then
 * closes itself as soon as the mark-in lands (or on "Refresh").
 *
 * UI ported from assets/workman-dashboard redesign — wm-modal* + wm-mi*
 * classes (see styles/wm-redesign.css). REQUIRED badge in header (no close
 * button).
 */
export function AttendanceGuardModal() {
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const enabled =
    role === "admin" || role === "operations" || role === "employee";

  const att = useTodayAttendance(enabled);
  const me = useMe(enabled);
  const todayHoliday = useTodayHoliday(enabled);
  const isSunday     = new Date().getDay() === 0;
  // Defensive client-side date match — guards against stale cache or any
  // tz mismatch on the server side so the modal is never wrongly hidden
  // by a holiday row whose date is not actually today.
  const todayLocalIso = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, "0");
    const dd = d.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${dd}`;
  })();
  const todayHolidayMatch =
    todayHoliday.isSuccess &&
    todayHoliday.data != null &&
    todayHoliday.data.date === todayLocalIso;
  const isHoliday = isSunday || todayHolidayMatch;
  const navigate = useNavigate();
  const location = useLocation();
  const myLocations = me.data?.locations ?? [];
  const isOpenMode = me.data?.user.attendanceLocationMode === "open";
  const fences: FencePoint[] = myLocations.map((loc) => ({
    lat: loc.lat,
    lng: loc.lng,
    radiusM: loc.radiusM,
    name: loc.name,
    isPrimary: loc.isPrimary,
  }));

  const [, setTick] = useState(0);

  const state = att.data;
  const hasAttendance = !!state?.attendance;
  const onLeaveToday = !!state?.onLeaveToday;
  const hasAssignedLocations = !!state?.hasAssignedLocations;
  // Leave-management is the sole whitelisted route while guard is active
  // (mirrors legacy UniversalLayout pattern). Modal silently hides there so
  // the user can file leave; the route guard handles forced redirect.
  const onLeavePage = location.pathname.startsWith("/leaves");
  const open =
    enabled &&
    att.isFetched &&
    todayHoliday.isFetched &&
    !hasAttendance &&
    !onLeaveToday &&
    !onLeavePage &&
    !isHoliday;

  // Clock tick while open.
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [open]);

  // Pick up a mark-in made on the phone without a manual refresh.
  const { refetch } = att;
  useEffect(() => {
    if (!open) return undefined;
    const t = setInterval(() => void refetch(), 15_000);
    return () => clearInterval(t);
  }, [open, refetch]);

  if (!open) return null;

  const today = new Date();
  // Open-mode users mark from anywhere — no assigned location needed.
  const noLocationBlock = !hasAssignedLocations && !isOpenMode;

  const goRequestLeave = () => {
    navigate("/leaves?new=1");
  };

  return createPortal(
    <div className="wm-modal-overlay">
      <div
        className="wm-modal"
        style={{
          width: "min(560px, calc(100vw - 24px))",
          maxHeight: "calc(100vh - 24px)",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="wm-modal-header">
          <div className="wm-modal-icon">
            <svg
              viewBox="0 0 16 16"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="8" cy="8" r="6.5" />
              <path d="M8 4v4l2.5 2" strokeLinecap="round" />
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="wm-modal-title">Mark attendance — in</div>
            <div className="wm-modal-sub">
              Required to continue. Cannot be dismissed.
            </div>
          </div>
          <span className="wm-modal-lock">REQUIRED</span>
        </div>

        {/* Body */}
        <div className="wm-modal-body">
          {/* Clock + date */}
          <div className="wm-mi-clock">
            <div className="l">
              <div className="t">Today</div>
              <div className="v">
                {today.toLocaleDateString("en-IN", {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })}
              </div>
            </div>
            <div className="r">
              <div className="t">Current time</div>
              <div className="v mono">
                {today.getHours().toString().padStart(2, "0")}
                <span style={{ opacity: 0.4 }}>:</span>
                {today.getMinutes().toString().padStart(2, "0")}
                <span style={{ opacity: 0.4 }}>:</span>
                <span style={{ fontSize: ".55em", opacity: 0.6 }}>
                  {today.getSeconds().toString().padStart(2, "0")}
                </span>
              </div>
            </div>
          </div>

          {noLocationBlock ? (
            <div className="wm-att-banner warn">
              <strong>No work location assigned.</strong>
              <span>
                You can't mark attendance yet. Ask HR / Operations to assign a
                location, or request leave for today.
              </span>
            </div>
          ) : (
            <div className="wm-att-banner" role="note">
              <strong>Mark in from the Workman mobile app.</strong>
              <span>
                Attendance can only be marked on your registered phone. Once
                you've marked in there, this closes on its own.
              </span>
            </div>
          )}

          {/* Assigned locations */}
          {fences.length > 0 && (
            <div className="wm-mi-loc-stack">
              <GeofenceLiveMap fences={fences} user={null} within={null} height={220} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="wm-modal-footer"
          style={{ flexDirection: "column", gap: 10, alignItems: "stretch" }}
        >
          {!noLocationBlock && (
            <button
              type="button"
              className="wm-btn primary"
              onClick={() => void refetch()}
              disabled={att.isFetching}
              style={{ fontSize: 13, padding: "8px 14px" }}
            >
              {att.isFetching ? "Checking…" : "I've marked in — refresh"}
            </button>
          )}
          <button
            type="button"
            className="wm-btn sm"
            onClick={goRequestLeave}
            style={{
              alignSelf: noLocationBlock ? "stretch" : "center",
              fontSize: 13,
              padding: "8px 14px",
            }}
          >
            Request leave for today instead
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
